import React, { useState, useEffect } from 'react';
import { Users, Mail, Calendar, Search, Download, Trash2, Filter, X, CheckCircle } from 'lucide-react';
import { getAllSubscribers, deleteSubscriber } from '../firebase/subscribers';
import { useToast } from './Toast';
import { TableSkeleton } from './LoadingStates';

export function AdminSubscribersTab({ showToast }) {
  const [subscribers, setSubscribers] = useState([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => {
    loadSubscribers();
  }, []);

  useEffect(() => {
    filterSubscribers();
  }, [subscribers, searchTerm, statusFilter, sourceFilter]);

  const loadSubscribers = async () => {
    setLoading(true);
    try {
      const data = await getAllSubscribers();
      setSubscribers(data);
    } catch (error) {
      console.error('Error loading subscribers:', error);
      showToast('Failed to load subscribers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterSubscribers = () => {
    let filtered = [...subscribers];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(sub => 
        sub.name?.toLowerCase().includes(term) ||
        sub.email?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }

    // Source filter
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(sub => sub.source === sourceFilter);
    }

    setFilteredSubscribers(filtered);
  };

  const handleDelete = async (subscriberId, email) => {
    if (!confirm(`Are you sure you want to remove ${email} from your subscriber list?`)) {
      return;
    }

    try {
      const result = await deleteSubscriber(subscriberId);
      if (result.success) {
        showToast('Subscriber removed successfully', 'success');
        loadSubscribers();
      } else {
        showToast('Error removing subscriber: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error removing subscriber: ' + error.message, 'error');
    }
  };

  const exportSubscribers = () => {
    const escapeCSV = (str) => {
      if (!str) return '';
      const strValue = String(str);
      const doubleQuote = '"';
      return strValue.split(doubleQuote).join(doubleQuote + doubleQuote);
    };
    
    const csv = [
      ['Name', 'Email', 'Source', 'Status', 'Date Subscribed'].join(','),
      ...filteredSubscribers.map(sub => [
        `"${escapeCSV(sub.name)}"`,
        sub.email,
        sub.source || 'unknown',
        sub.status || 'active',
        sub.createdAt?.toDate?.()?.toISOString() || sub.createdAt || ''
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateStr = new Date().toISOString().split('T')[0];
    a.download = `subscribers-${dateStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Subscribers exported successfully', 'success');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const activeCount = subscribers.filter(s => s.status === 'active').length;
  const totalCount = subscribers.length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-serif text-gray-800 mb-2">Subscribers</h2>
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#c5a059]"></div>
              <span className="font-medium">{totalCount}</span> total
            </span>
            <span className="flex items-center gap-2 text-[#d48c95]">
              <CheckCircle size={16} className="text-green-500" />
              <span className="font-medium">{activeCount}</span> active
            </span>
          </div>
        </div>
        <button
          onClick={exportSubscribers}
          disabled={filteredSubscribers.length === 0}
          className="px-6 py-3 bg-[#c5a059] text-white rounded-full font-medium shadow-lg hover:bg-[#b08e4d] hover:shadow-xl transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#c5a059] disabled:hover:shadow-lg"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* Filters - Landing Page Style */}
      <div className="bg-gradient-to-br from-[#fae1e5]/30 to-[#f3e6d5]/30 rounded-2xl border border-[#eaddcf] p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#c5a059]" size={18} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/80 border border-[#eaddcf] rounded-xl focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059] focus:bg-white transition-all placeholder:text-gray-400"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 bg-white/80 border border-[#eaddcf] rounded-xl focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059] focus:bg-white transition-all text-gray-700"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="unsubscribed">Unsubscribed</option>
          </select>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-4 py-3 bg-white/80 border border-[#eaddcf] rounded-xl focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059] focus:bg-white transition-all text-gray-700"
          >
            <option value="all">All Sources</option>
            <option value="meditation_form">Meditation Form</option>
            <option value="manual">Manual</option>
          </select>
        </div>

        {(searchTerm || statusFilter !== 'all' || sourceFilter !== 'all') && (
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setSourceFilter('all');
            }}
            className="mt-4 text-[#c5a059] hover:text-[#b08e4d] transition-colors text-sm flex items-center gap-2 font-medium"
          >
            <X size={16} />
            Clear filters
          </button>
        )}
      </div>

      {/* Subscribers Cards - Landing Page Style */}
      {loading ? (
        <TableSkeleton rows={5} />
      ) : filteredSubscribers.length === 0 ? (
        <div className="bg-gradient-to-br from-[#fae1e5]/20 to-[#f3e6d5]/20 rounded-2xl border border-[#eaddcf] p-16 text-center">
          <div className="w-20 h-20 bg-[#fae1e5] rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-[#d48c95]" />
          </div>
          <p className="text-lg text-gray-600 font-medium">
            {subscribers.length === 0 
              ? 'No subscribers yet' 
              : 'No subscribers match your filters'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredSubscribers.map((subscriber) => (
            <div
              key={subscriber.id}
              className="bg-gradient-to-br from-white to-[#fdfbf7] rounded-2xl border border-[#eaddcf] p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-[#c5a059]/30"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                {/* Left Section - Main Info */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#fae1e5] to-[#f3e6d5] flex items-center justify-center flex-shrink-0">
                      <span className="text-lg font-serif text-[#c5a059] font-semibold">
                        {(subscriber.name || subscriber.email)[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-serif text-gray-800 mb-1">
                        {subscriber.name || 'No name provided'}
                      </h3>
                      <a
                        href={`mailto:${subscriber.email}`}
                        className="text-sm text-gray-600 hover:text-[#c5a059] transition-colors flex items-center gap-2 group"
                      >
                        <Mail size={16} className="text-[#d48c95] group-hover:text-[#c5a059] transition-colors" />
                        <span className="truncate">{subscriber.email}</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Right Section - Meta Info & Actions */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-[#fae1e5] text-[#d48c95] border border-[#d48c95]/20">
                      {subscriber.source || 'unknown'}
                    </span>
                    <span
                      className={`px-3 py-1.5 text-xs font-medium rounded-full ${
                        subscriber.status === 'active'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : subscriber.status === 'unsubscribed'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-gray-50 text-gray-700 border border-gray-200'
                      }`}
                    >
                      {subscriber.status || 'active'}
                    </span>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar size={14} className="text-[#c5a059]" />
                      <span>{formatDate(subscriber.createdAt)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(subscriber.id, subscriber.email)}
                    className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 flex-shrink-0"
                    title="Remove subscriber"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

